/**
 * The Gateways tab's rows (user, 19/10/2026: "a gateway overview — what a
 * client who automates everything needs to see"): one row per gateway the
 * day touches, built from the day plan preview (planned and sent drops per
 * container, estimated cost) and the overview read (real rates from today's
 * sends, the blocks the verdicts placed, the route tests' winners, prices).
 * Status: blocked (country-wide, or in some containers) · failing (a send
 * SMSWiser refused today, unresolved — the error trace, 19/10/2026) · winner
 * · testing · ok; "past limits" = rates past the usual verdict limits before any block.
 * Sorted trouble first, then by planned SMS.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanPreview, DropGenErrorRow, DropGenGatewayBlockRow, DropGenGatewayOverview, DropGenGatewayRates, DropGenRouteTestWinnerRow } from '@trafficpulse/shared';

export interface GatewayContainerUse {
  containerId: string;
  name: string;
  color: string;
  routeTest: boolean;
  planned: number;
  sent: number;
  sms: number;
}

export type GatewayStatus = 'blocked' | 'failing' | 'winner' | 'testing' | 'ok';

/** The usual gateway verdict limits: delivered under 85 %, bot clicks over 15 %. */
export const LIMITS = { deliveredBelow: 0.85, botAbove: 0.15 } as const;

/** Rates past the limits today, with stats behind them — "failing" before any verdict blocks it. */
export const pastLimits = (r: DropGenGatewayRates | null): boolean =>
  r !== null && r.dropsWithStats > 0 && ((r.deliveredRate !== null && r.deliveredRate < LIMITS.deliveredBelow) || (r.botRate !== null && r.botRate > LIMITS.botAbove));

const RANK: Record<GatewayStatus, number> = { blocked: 0, failing: 1, testing: 3, winner: 4, ok: 5 };
/** Trouble first: blocked, failing, then past the limits, then testing, winner, ok — the busiest first within each. */
export const trouble = (g: GatewayToday): number => (g.status === 'ok' && pastLimits(g.rates) ? 2 : RANK[g.status]);

export interface GatewayToday {
  routeId: number;
  name: string;
  pricePerSms: number | null;
  containers: GatewayContainerUse[];
  planned: { drops: number; sms: number };
  sent: { drops: number; sms: number };
  estCost: number | null;
  rates: DropGenGatewayRates | null;
  blocks: DropGenGatewayBlockRow[];
  /** Today's unresolved refused sends through it, newest first. */
  failures: DropGenErrorRow[];
  countryBlocked: boolean;
  winnerFor: DropGenRouteTestWinnerRow[];
  testing: boolean;
  status: GatewayStatus;
}

const live = (d: { runtime?: { tombstoned?: boolean } | null }) => !d.runtime?.tombstoned;

function blankRow(routeId: number, name: string, pricePerSms: number | null): GatewayToday {
  return { routeId, name, pricePerSms, containers: [], planned: { drops: 0, sms: 0 }, sent: { drops: 0, sms: 0 }, estCost: 0, rates: null, blocks: [], failures: [], countryBlocked: false, winnerFor: [], testing: false, status: 'ok' };
}

type Container = DayPlanPreview['containers'][number];
type Drop = Container['drops'][number];

function addDrop(row: GatewayToday, c: Container, d: Drop): void {
  const sent = Boolean(d.runtime?.sentAt);
  row.planned.drops += 1;
  row.planned.sms += d.amount;
  if (sent) {
    row.sent.drops += 1;
    row.sent.sms += d.amount;
  }
  row.estCost = row.estCost === null || d.estCost === null ? null : row.estCost + d.estCost;
  if (d.routeTest) row.testing = true;
  const use = row.containers.find((u) => u.containerId === c.containerId);
  if (use) {
    use.planned += 1;
    use.sms += d.amount;
    if (sent) use.sent += 1;
  } else row.containers.push({ containerId: c.containerId, name: c.name, color: c.color, routeTest: Boolean(d.routeTest), planned: 1, sent: sent ? 1 : 0, sms: d.amount });
}

const statusOf = (row: GatewayToday): GatewayStatus =>
  row.countryBlocked || row.blocks.length > 0 ? 'blocked' : row.failures.length > 0 ? 'failing' : row.winnerFor.length > 0 ? 'winner' : row.testing ? 'testing' : 'ok';

const refused = (e: DropGenErrorRow) => (e.kind === 'send_failed' || e.kind === 'send_unknown') && e.routeId !== null && !e.resolvedAt;

export function gatewaysToday(preview: DayPlanPreview | undefined, overview: DropGenGatewayOverview | undefined, scopeIds: ReadonlySet<string> | null = null, errors: DropGenErrorRow[] = []): GatewayToday[] {
  const rows = new Map<number, GatewayToday>();
  const route = (id: number) => overview?.routes.find((r) => r.routeId === id);
  const rowOf = (id: number, name: string | null) => {
    const row = rows.get(id) ?? blankRow(id, route(id)?.name ?? name ?? `Gateway ${id}`, route(id)?.pricePerSms ?? null);
    rows.set(id, row);
    return row;
  };
  for (const c of preview?.containers ?? [])
    for (const d of c.drops) if (live(d) && d.routeId !== null) addDrop(rowOf(d.routeId, d.routeName), c, d);
  for (const b of overview?.blocks ?? []) {
    if (scopeIds && !scopeIds.has(b.containerId)) continue;
    const row = rowOf(b.routeId, b.routeName);
    row.blocks.push(b);
    if (b.scope === 'COUNTRY') row.countryBlocked = true;
  }
  for (const e of errors) {
    if (!refused(e) || (scopeIds && e.containerId && !scopeIds.has(e.containerId))) continue;
    rowOf(e.routeId!, e.routeName).failures.push(e);
  }
  for (const w of overview?.winners ?? []) {
    if (scopeIds && !w.appliedTo.some((c) => scopeIds.has(c.containerId)) && !scopeIds.has(w.masterId)) continue;
    rowOf(w.routeId, w.routeName).winnerFor.push(w);
  }
  for (const row of rows.values()) {
    row.rates = overview?.rates.find((r) => r.routeId === row.routeId && r.containerId === null) ?? null;
    row.status = statusOf(row);
  }
  return [...rows.values()].sort((a, b) => trouble(a) - trouble(b) || b.planned.sms - a.planned.sms || a.name.localeCompare(b.name));
}
